import { useEffect, useMemo, useRef, useState } from "react";
import { UserPlus, Plus, Trash2, Check, Clock, Pencil, Minus, MessageSquare, Eye, Lock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { VariableTextEditor, type VariableTextEditorHandle } from "@/components/campaigns/VariableTextEditor";
import { VariableChips } from "@/components/campaigns/VariableChips";
import { VariablePicker } from "@/components/campaigns/VariablePicker";
import {
  listTemplateTokens,
  ENRICHMENT_VARIABLES,
  findEnrichmentTagsUsed,
  parseVariableToken,
} from "@/lib/messageVariables";

/** Full step shape; extra fields (fallback, inmail, attachments…) are preserved on edit. */
export interface TimelineStep {
  step_number: number;
  action_type: "connect" | "message" | "end";
  delay_hours: number;
  message_template?: string;
  include_message?: boolean;
  connection_timeout_days?: number;
  attachment_urls?: string[];
  use_inmail?: boolean;
  fallback_template?: string | null;
  fallback_enabled?: boolean | null;
  [key: string]: any;
}

interface Props {
  sequences: TimelineStep[];
  onChange: (sequences: any[]) => void;
  isPremium?: boolean;
  /** Campaign status: active → text edits only; completed → read-only. */
  campaignStatus?: string;
  /** CSV column names from uploaded targets, offered as variables. */
  availableVariables?: string[];
  /** Super-admin advanced profile variables + whole-message fallback. */
  allowEnrichmentVariables?: boolean;
  /** Targets used for the live preview dialog. */
  targets?: any[];
}

const BASE_VARS = ["first_name", "last_name", "company", "title"];
const LABEL: Record<string, string> = { first_name: "First name", last_name: "Last name", company: "Company", title: "Job title" };
const SYSTEM_FIELDS = ["linkedin_url", "link_type", "sales_nav_url", "profile_url", "provider_id"];
const ALIASES: Record<string, string> = { firstname: "first_name", lastname: "last_name", first: "first_name", last: "last_name", jobtitle: "title", job_title: "title" };
const norm = (n: string) => n.toLowerCase().trim().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
const pretty = (n: string) => LABEL[n] || n.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

function RichPreview({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let cur = 0;
  for (const t of listTemplateTokens(text)) {
    if (t.start > cur) parts.push(text.slice(cur, t.start));
    parts.push(
      <span key={t.start} className="mx-0.5 inline-flex items-center rounded-full bg-primary/10 px-2 py-px align-baseline text-xs font-medium text-primary ring-1 ring-primary/20">
        {pretty(t.name)}
      </span>,
    );
    cur = t.end;
  }
  if (cur < text.length) parts.push(text.slice(cur));
  return <>{parts}</>;
}

/** Visual, click-to-edit view of an outreach sequence (connect → messages → end). */
export function SequenceTimeline({ sequences, onChange, isPremium, campaignStatus, availableVariables = [], allowEnrichmentVariables = false, targets = [] }: Props) {
  const [open, setOpen] = useState<number | null>(null);
  const [previewIdx, setPreviewIdx] = useState<number | null>(null);
  const [previewTarget, setPreviewTarget] = useState("");
  const snapshot = useRef<TimelineStep | null>(null);
  const editorRef = useRef<VariableTextEditorHandle>(null);
  const fallbackRef = useRef<VariableTextEditorHandle>(null);
  const steps = sequences.filter((s) => s.action_type !== "end");
  const msgCount = steps.filter((s) => s.action_type === "message").length;
  const noteLimit = isPremium ? 300 : 200;
  const readOnly = campaignStatus === "completed";
  const canModifyStructure = !campaignStatus || campaignStatus === "draft" || campaignStatus === "paused";

  // Variables: base four (or those present in CSV) + custom CSV columns.
  const vars = useMemo(() => {
    if (!availableVariables.length) return BASE_VARS;
    const csv = availableVariables.map(norm);
    const base = BASE_VARS.filter((v) => csv.includes(v) || Object.entries(ALIASES).some(([a, c]) => c === v && csv.includes(a)));
    const custom = csv.filter((v) => !BASE_VARS.includes(v) && !ALIASES[v] && !SYSTEM_FIELDS.includes(v));
    return [...new Set([...base, ...custom])];
  }, [availableVariables]);
  const known = [...vars, ...(allowEnrichmentVariables ? (ENRICHMENT_VARIABLES as readonly string[]) : [])];

  const problems = (t: string) => {
    if (!t) return [];
    const enr = findEnrichmentTagsUsed(t);
    const used = [...t.matchAll(/\{([^}]+)\}/g)].map((m) => { const n = norm(parseVariableToken(m[1]).name); return ALIASES[n] || n; });
    const bad = used.filter((v) => !vars.includes(v) && !enr.includes(v));
    if (!allowEnrichmentVariables) bad.push(...enr);
    return [...new Set(bad)];
  };

  const commit = (next: TimelineStep[]) => {
    const body = next.filter((s) => s.action_type !== "end").map((s, i) => ({ ...s, step_number: i + 1 }));
    onChange([...body, { step_number: body.length + 1, action_type: "end", delay_hours: 0 }]);
  };
  const patch = (i: number, p: Partial<TimelineStep>) => commit(steps.map((s, j) => (j === i ? { ...s, ...p } : s)));
  const openStep = (i: number) => { if (readOnly) return; snapshot.current = { ...steps[i] }; setOpen(i); };
  const cancel = () => {
    if (open !== null && snapshot.current) patch(open, snapshot.current);
    setOpen(null);
  };
  const remove = (i: number) => { if (!canModifyStructure) return; commit(steps.filter((_, j) => j !== i)); setOpen(null); };
  const add = () => {
    const next = [...steps, { step_number: 0, action_type: "message" as const, delay_hours: 72, message_template: "" }];
    commit(next);
    snapshot.current = null;
    setOpen(next.length - 1);
  };

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Preview
  const previewTargets = useMemo(() => (targets || []).slice(0, 5).map((t, i) => ({ ...t, _pid: t.id ? String(t.id) : `p-${i}` })), [targets]);
  useEffect(() => {
    if (previewIdx !== null && previewTargets.length && !previewTargets.some((t) => t._pid === previewTarget)) setPreviewTarget(previewTargets[0]._pid);
  }, [previewIdx, previewTargets, previewTarget]);
  const renderPreview = (tpl: string) => {
    const t = previewTargets.find((x) => x._pid === previewTarget);
    if (!t) return tpl;
    return tpl.replace(/\{([^}]+)\}/g, (m, v) => {
      const c = ALIASES[norm(v)] || norm(v);
      if (t[c] != null && t[c] !== "") return String(t[c]);
      const k = t.custom_data && Object.keys(t.custom_data).find((x) => norm(x) === c);
      return k && t.custom_data[k] ? String(t.custom_data[k]) : m;
    });
  };

  let day = 0;
  let m = 0;
  return (
    <div className="flex flex-col items-center py-2">
      {(readOnly || !canModifyStructure) && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" />
          {readOnly ? "Campaign completed · sequence is read-only" : "Campaign is running · you can edit messages and waits; pause to add or remove steps"}
        </div>
      )}
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Start</span>
      <Line />
      {steps.map((s, i) => {
        const waitDays = Math.round((s.delay_hours || 0) / 24);
        day += waitDays;
        const isConnect = s.action_type === "connect";
        const text = s.message_template ?? "";
        const hasText = !!text.trim();
        const editing = open === i;
        const limit = isConnect ? noteLimit : 1900;
        const nearLimit = text.length > limit * 0.9;
        const msgNo = isConnect ? 0 : ++m;
        const typeLabel = isConnect ? "Connection request" : msgNo === 1 ? "First message" : `Follow-up ${msgNo - 1}`;
        const prevConnect = i > 0 && steps[i - 1].action_type === "connect";
        const condition = prevConnect ? "When accepted" : "If no reply";
        const bad = problems(text);
        const showFallback = allowEnrichmentVariables && (!isConnect || hasText);

        const editor = (
          <div className="mt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
            <VariableTextEditor
              ref={editorRef}
              value={text}
              onChange={(v) => patch(i, { message_template: isConnect ? v.slice(0, limit) : v, ...(isConnect ? { include_message: !!v.trim() } : {}) })}
              maxLength={limit}
              knownVariables={known}
              placeholder={isConnect ? "Optional invite note" : "Write your message"}
            />
            <div className="space-y-2 rounded-lg bg-background/60 p-2 ring-1 ring-border/60">
              <VariablePicker basicVariables={vars} allowEnrichmentVariables={allowEnrichmentVariables} onInsert={(tok) => editorRef.current?.insertAtCursor(tok)} />
              <div className="flex items-start justify-between gap-3">
                <VariableChips value={text} onChange={(v) => patch(i, { message_template: v })} />
                <span className={cn("shrink-0 text-[11px] tabular-nums", nearLimit ? "text-destructive" : "text-muted-foreground")}>{text.length}/{limit}</span>
              </div>
            </div>

            {showFallback && (
              <div className="space-y-2 rounded-lg border border-dashed p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="text-xs font-medium">Whole-message fallback</Label>
                    <p className="text-[11px] text-muted-foreground">If any advanced variable has no value for a target, this message is sent instead.</p>
                  </div>
                  <Switch checked={!!s.fallback_enabled} onCheckedChange={(c) => patch(i, { fallback_enabled: c, fallback_template: s.fallback_template || "" })} />
                </div>
                {s.fallback_enabled && (
                  <>
                    <VariableTextEditor
                      ref={fallbackRef}
                      value={s.fallback_template || ""}
                      onChange={(v) => patch(i, { fallback_template: v })}
                      maxLength={isConnect ? noteLimit : undefined}
                      knownVariables={vars}
                      placeholder="Hi {first_name}, quick question about your work at {company}..."
                    />
                    <VariablePicker basicVariables={vars} allowEnrichmentVariables={false} onInsert={(tok) => fallbackRef.current?.insertAtCursor(tok)} />
                  </>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              {!isConnect && canModifyStructure ? (
                <Button type="button" size="sm" variant="ghost" className="gap-1 text-destructive hover:text-destructive" onClick={() => remove(i)}><Trash2 className="h-3.5 w-3.5" /> Remove</Button>
              ) : <span />}
              <div className="flex gap-2">
                {previewTargets.length > 0 && hasText && (
                  <Button type="button" size="sm" variant="ghost" className="gap-1" onClick={() => setPreviewIdx(i)}><Eye className="h-3.5 w-3.5" /> Preview</Button>
                )}
                <Button type="button" size="sm" variant="outline" onClick={cancel}>Cancel</Button>
                <Button type="button" size="sm" className="gap-1" onClick={() => setOpen(null)}><Check className="h-3.5 w-3.5" /> Done</Button>
              </div>
            </div>
          </div>
        );

        return (
          <div key={i} className="flex w-full flex-col items-center">
            {i > 0 && (
              <>
                <Connector label={condition} days={waitDays} hours={s.delay_hours || 0} disabled={readOnly} onChange={(d) => patch(i, { delay_hours: d * 24 })} />
                <Line />
              </>
            )}
            <span className={cn("z-10 -mb-3 inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ring-4 ring-background", isConnect ? "bg-info text-background" : "bg-primary text-primary-foreground")}>Step {i + 1}</span>
            <div
              role={editing || readOnly ? undefined : "button"}
              tabIndex={editing || readOnly ? undefined : 0}
              onClick={() => !editing && openStep(i)}
              onKeyDown={(e) => !editing && e.key === "Enter" && openStep(i)}
              className={cn(
                "group w-full max-w-[640px] rounded-2xl border px-5 pb-4 pt-6 transition-colors",
                isConnect ? "border-info/20 bg-info/5" : "border-primary/15 bg-primary/5",
                editing ? "border-primary/50 shadow-sm" : !readOnly && cn("cursor-pointer", isConnect ? "hover:border-info/40" : "hover:border-primary/40"),
              )}
            >
              <div className="flex items-center gap-2.5">
                <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", isConnect ? "bg-info/15 text-info" : "bg-primary/15 text-primary")}>
                  {isConnect ? <UserPlus className="h-4 w-4" /> : <MessageSquare className="h-4 w-4" />}
                </span>
                <span className="text-sm font-semibold text-foreground">{typeLabel}</span>
                <span className="text-sm text-muted-foreground">· Day {day}</span>
                {s.fallback_enabled && <span className="rounded-full bg-muted px-2 py-px text-[10px] font-medium text-muted-foreground">Fallback on</span>}
                <span className="ml-auto flex items-center gap-1">
                  {readOnly ? null : isConnect && !hasText && !editing ? (
                    <span className="text-xs font-medium text-info group-hover:underline">+ Add note</span>
                  ) : !editing && (
                    <>
                      {previewTargets.length > 0 && hasText && (
                        <button type="button" aria-label="Preview" onClick={(e) => { e.stopPropagation(); setPreviewIdx(i); }}
                          className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"><Eye className="h-3.5 w-3.5" /></button>
                      )}
                      <span className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"><Pencil className="h-3.5 w-3.5" /></span>
                      {!isConnect && canModifyStructure && (
                        <button type="button" aria-label="Delete step" onClick={(e) => { e.stopPropagation(); remove(i); }}
                          className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </>
                  )}
                </span>
              </div>
              {editing ? editor : hasText ? (
                <div className={cn("mt-3 rounded-2xl rounded-bl-md border bg-card px-4 py-3 shadow-sm", isConnect ? "border-info/15" : "border-primary/10")}>
                  <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/85"><RichPreview text={text} /></p>
                </div>
              ) : !isConnect ? (
                <p className="mt-3 text-sm italic text-muted-foreground">Empty — click to write</p>
              ) : (
                <p className="mt-2 pl-[42px] text-xs text-muted-foreground">No note · sends a blank invite</p>
              )}
              {bad.length > 0 && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5" /> Unknown variable{bad.length > 1 ? "s" : ""}: {bad.map((b) => `{${b}}`).join(", ")}
                </p>
              )}
            </div>
            <Line />
          </div>
        );
      })}
      {msgCount < 7 && canModifyStructure && !readOnly && (
        <>
          <button type="button" onClick={add} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
            <Plus className="h-4 w-4" /> Add follow-up
          </button>
          <Line />
        </>
      )}
      <span className="text-xs text-muted-foreground">Ends when they reply</span>

      <Dialog open={previewIdx !== null} onOpenChange={(o) => !o && setPreviewIdx(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Message preview</DialogTitle>
            <DialogDescription>See how this message looks for a real target.</DialogDescription>
          </DialogHeader>
          {previewTargets.length > 0 && (
            <Select value={previewTarget} onValueChange={setPreviewTarget}>
              <SelectTrigger><SelectValue placeholder="Choose a target" /></SelectTrigger>
              <SelectContent>
                {previewTargets.map((t) => (
                  <SelectItem key={t._pid} value={t._pid}>{[t.first_name, t.last_name].filter(Boolean).join(" ") || t.company || "Target"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="rounded-2xl rounded-bl-md border bg-card px-4 py-3 shadow-sm">
            <p className="whitespace-pre-wrap text-sm leading-relaxed">{previewIdx !== null ? renderPreview(steps[previewIdx]?.message_template || "") : ""}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Line() {
  return <span className="h-6 w-px bg-border" />;
}

function Connector({ label, days, hours, onChange, disabled }: { label: string; days: number; hours: number; onChange: (d: number) => void; disabled?: boolean }) {
  return (
    <div className="group/wait my-1 inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 py-1 pl-3 pr-1 text-xs shadow-sm">
      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="font-medium text-foreground">{hours % 24 !== 0 ? `Wait ${hours} hour${hours === 1 ? "" : "s"}` : `Wait ${days} day${days === 1 ? "" : "s"}`}</span>
      <span className="text-muted-foreground">· {label.toLowerCase()}</span>
      {!disabled && (
        <span className="ml-1 inline-flex items-center rounded-full bg-background ring-1 ring-border">
          <button type="button" aria-label="Fewer days" disabled={days <= 1} onClick={() => onChange(Math.max(1, days - 1))} className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"><Minus className="h-3 w-3" /></button>
          <button type="button" aria-label="More days" disabled={days >= 14} onClick={() => onChange(Math.min(14, days + 1))} className="flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"><Plus className="h-3 w-3" /></button>
        </span>
      )}
    </div>
  );
}
